import { z } from 'zod';
import { RegionsSchema } from './regions.js';
import { AnnotationLabelSchema, DefectClassificationSchema, RoleSchema, VerdictSchema, } from './verdicts.js';
/** A product line whose units get inspected. */
export const ProductSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    sku: z.string(),
    active: z.boolean(),
    /** Score >= threshold is classified BAD (higher score = more anomalous). */
    threshold: z.number().min(0).max(1),
    /**
     * Half-width of the UNSURE band around the threshold. |score - threshold| <=
     * band => the model answers UNSURE instead of guessing. 0 disables it.
     */
    uncertaintyBand: z.number().min(0).max(0.5),
    modelVersion: z.string().nullable(),
    customerId: z.number().int().positive().nullable(),
    createdAt: z.string(),
    /**
     * Which question this product's line asks — see ./tasks.ts.
     *
     * `threshold` and `uncertaintyBand` above belong to `anomaly` alone. A
     * classification product carries its thresholds in the `thresholds` table
     * instead, because one number cannot express "this class is harder than that
     * one" and because a threshold is a dated decision rather than a setting.
     */
    task: z.enum(['anomaly', 'classification']).default('anomaly'),
});
/**
 * A named, colored defect category belonging to a product. Each product has its
 * own vocabulary (ballbearings ≠ cookies), and the name carries meaning: the
 * classification says whether choosing it means GOOD, BAD or UNSURE.
 */
export const DefectTypeSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    /** Hex color, e.g. "#ff3b30". Used for region overlays and pickers. */
    color: z.string(),
    /** Outcome implied by picking this defect. */
    classification: DefectClassificationSchema,
    /** Optional guidance shown to the operator while annotating. */
    description: z.string().nullable(),
    sortOrder: z.number().int(),
});
/** A single inspection result (a time-series row / hypertable record). */
export const InspectionSchema = z.object({
    /** Which station produced it. Null on the local line. */
    stationId: z.number().int().positive().nullable().default(null),
    /** Which question this inspection answered — see ./tasks.ts. */
    task: z.enum(['anomaly', 'classification']).default('anomaly'),
    /** What the classifier said, and what was done about it. Null on anomaly. */
    classId: z.number().int().positive().nullable().default(null),
    classConfidence: z.number().nullable().default(null),
    decision: z.enum(['auto', 'reject', 'override']).nullable().default(null),
    /** Ground truth after a human correction — kept apart from what the model said,
        or a corrected row would read as one the model got right. */
    finalClassId: z.number().int().positive().nullable().default(null),
    id: z.string().uuid(),
    time: z.string(),
    productId: z.number().int().positive(),
    /** Null on a classification inspection, which names a class instead. */
    verdict: VerdictSchema.nullable(),
    score: z.number(),
    defectTypeId: z.number().int().positive().nullable(),
    imageKey: z.string(),
    heatmapKey: z.string().nullable(),
    modelVersion: z.string(),
    latencyMs: z.number().int().nonnegative(),
});
/** A human label for an image, used to build training data. */
export const AnnotationSchema = z.object({
    id: z.string().uuid(),
    inspectionId: z.string().uuid().nullable(),
    imageKey: z.string(),
    /** Which question this annotation answers — see ./tasks.ts. */
    task: z.enum(['anomaly', 'classification']).default('anomaly'),
    /** A quality judgement. Null on a classification annotation, which asserts an
        identity instead — exactly one of label and classId is filled. */
    label: AnnotationLabelSchema.nullable(),
    classId: z.number().int().positive().nullable().default(null),
    defectTypeId: z.number().int().positive().nullable(),
    regions: RegionsSchema,
    annotatedBy: z.string().nullable(),
    createdAt: z.string(),
    synced: z.boolean(),
    /** Who decided: a person, the model (awaiting review), or a reviewed proposal. */
    source: z.enum(['human', 'model', 'confirmed']).default('human'),
});
/** An operator/admin account (PIN auth stubbed for phase 0+1). */
export const UserSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    role: RoleSchema,
});
//# sourceMappingURL=entities.js.map